package org.wordsapp.ui.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.wordsapp.currentTimeMillis
import org.wordsapp.model.Word
import org.wordsapp.theme.*
import org.wordsapp.viewmodel.WordViewModel

/**
 * Bucket edges for the final weight (= urgency x difficulty). Not-yet-due words have
 * urgency 1..2, overdue 2..11, new 5; difficulty spans 1..~6, so weights run 1..66.
 */
private val WEIGHT_EDGES = listOf(1.0, 1.25, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 7.0, 10.0, 14.0, 20.0, 30.0, 45.0)

private data class BucketStats(
    val label: String,
    val total: Int,
    val due: Int,
    val weightSum: Double,
)

@Composable
fun WeightDistributionScreen(viewModel: WordViewModel, onBack: () -> Unit) {
    val words by viewModel.words.collectAsState()
    val now = remember(words) { currentTimeMillis() }

    val buckets = remember(words, now) { bucketize(words, now) }
    val totalWords = words.size
    val totalWeight = words.sumOf { it.weight(now) }
    val maxCount = buckets.maxOfOrNull { it.total } ?: 0
    val dueWords = words.filter { it.isDue(now) }
    val dueWeight = dueWords.sumOf { it.weight(now) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp)
            .verticalScroll(rememberScrollState()),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            IconButton(onClick = onBack) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
            }
            Text(
                text = "Weight distribution",
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
            )
        }

        Spacer(Modifier.height(8.dp))

        Card(
            shape = RoundedCornerShape(12.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        ) {
            Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    "$totalWords words, total weight ${format1(totalWeight)}",
                    style = MaterialTheme.typography.bodyMedium,
                )
                Text(
                    "${dueWords.size} due (incl. new) hold ${format1(dueWeight)} — " +
                        "${percent(dueWeight, totalWeight)} of the total",
                    style = MaterialTheme.typography.bodyMedium,
                )
                Text(
                    "weight = urgency (1–2 not due, 2–11 overdue, 5 new) × difficulty (1 for always-correct, ~6 for always-wrong). " +
                        "Draws come from the due tier only while it is non-empty.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }

        Spacer(Modifier.height(16.dp))

        buckets.forEach { bucket ->
            BucketRow(
                bucket = bucket,
                fraction = if (maxCount > 0) bucket.total.toDouble() / maxCount else 0.0,
                share = percent(bucket.total.toDouble(), totalWords.toDouble()),
                weightShare = percent(bucket.weightSum, totalWeight),
            )
            Spacer(Modifier.height(8.dp))
        }
    }
}

private fun bucketize(words: List<Word>, now: Long): List<BucketStats> {
    val weighted = words.map { it to it.weight(now) }
    return WEIGHT_EDGES.indices.map { i ->
        val lo = WEIGHT_EDGES[i]
        val hi = WEIGHT_EDGES.getOrNull(i + 1)
        val inBucket = weighted.filter { (_, w) -> w >= lo && (hi == null || w < hi) }
        BucketStats(
            label = if (hi == null) "${formatEdge(lo)}+" else "${formatEdge(lo)} – ${formatEdge(hi)}",
            total = inBucket.size,
            due = inBucket.count { (word, _) -> word.isDue(now) },
            weightSum = inBucket.sumOf { (_, w) -> w },
        )
    }
}

@Composable
private fun BucketRow(
    bucket: BucketStats,
    fraction: Double,
    share: String,
    weightShare: String,
) {
    val notDue = bucket.total - bucket.due
    Column {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(bucket.label, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Medium)
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    "${bucket.total} words · $share",
                    style = MaterialTheme.typography.bodyMedium,
                )
                Text(
                    "${bucket.due} due, $notDue not due · $weightShare of weight",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        Spacer(Modifier.height(4.dp))
        // Bar length = word count; the red part is the due share of that bucket.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(10.dp)
                .clip(RoundedCornerShape(5.dp))
                .background(Gray400.copy(alpha = 0.2f)),
        ) {
            Row(modifier = Modifier.fillMaxWidth(fraction.toFloat().coerceIn(0f, 1f)).fillMaxHeight()) {
                if (bucket.due > 0) {
                    Box(
                        Modifier
                            .weight(bucket.due.toFloat())
                            .fillMaxHeight()
                            .background(Red500),
                    )
                }
                if (notDue > 0) {
                    Box(
                        Modifier
                            .weight(notDue.toFloat())
                            .fillMaxHeight()
                            .background(Gray400),
                    )
                }
            }
        }
    }
}

private fun formatEdge(value: Double): String =
    if (value == value.toLong().toDouble()) value.toLong().toString() else format2(value)

private fun format1(value: Double): String {
    val tenths = (value.coerceAtLeast(0.0) * 10 + 0.5).toLong()
    return "${tenths / 10}.${tenths % 10}"
}

private fun format2(value: Double): String {
    val hundredths = (value.coerceAtLeast(0.0) * 100 + 0.5).toLong()
    val frac = (hundredths % 100).toString().padStart(2, '0').trimEnd('0')
    return "${hundredths / 100}.$frac"
}

private fun percent(part: Double, total: Double): String {
    if (total <= 0.0) return "0%"
    val p = part / total * 100
    return if (p >= 9.95) "${(p + 0.5).toInt()}%" else "${format1(p)}%"
}
